/**
 * Spec 041 — CommandCenterPanel (modified).
 *
 * Adds a tab bar with three tabs:
 *   • Threads — live list of equanaut threads (FR-003, SC-001)
 *   • Fleet   — live list of browser-agent fleet members (FR-004)
 *   • Status  — previous gateway/session/priority stack view (preserved)
 *
 * Threads and Fleet data are fetched from the new /api/equanaut/* endpoints
 * using useThreads and useFleet hooks (30-second poll, credentials: include).
 */

import React, { useState, useEffect } from 'react';
import { ActivityIcon, ListIcon, WifiIcon, AlertCircleIcon } from './Icons';
import { useAgentStatus, usePriorityStack, useSessionList } from '../hooks/useOrchestration';
import { useThreads } from '../hooks/useThreads';
import { useFleet } from '../hooks/useFleet';
import ThreadsTab from './ThreadsTab';
import FleetTab from './FleetTab';

interface GatewayStatus {
  connected: boolean;
  authOk: boolean;
  gatewayVersion?: string;
  uptime?: string;
  error?: string;
}

interface CommandCenterPanelProps {
  organizationId?: string;
  sessionKey: string;
  gatewayConnected?: boolean;
  initialTab?: CommandCenterTab;
}

export type CommandCenterTab = 'threads' | 'fleet' | 'status';

const PRIORITY_COLORS: Record<string, string> = {
  p0: '#FF5656',
  p1: '#F5A623',
  p2: '#33AA40',
  p3: '#8899A6',
};

const STATUS_COLORS: Record<string, string> = {
  active: '#33AA40',
  blocked: '#FF5656',
  pending: '#F5A623',
};

const CommandCenterPanel: React.FC<CommandCenterPanelProps> = ({
  organizationId,
  sessionKey,
  gatewayConnected = false,
  initialTab = 'threads',
}) => {
  const [activeTab, setActiveTab] = useState<CommandCenterTab>(initialTab);
  const [gatewayStatus, setGatewayStatus] = useState<GatewayStatus>({
    connected: gatewayConnected,
    authOk: false,
  });

  // Status tab data (legacy orchestration hooks)
  const { status } = useAgentStatus(organizationId, !!organizationId);
  const { sessions, isLoading: sessionsLoading } = useSessionList(organizationId, !!organizationId);
  const { priorities: tasks, isLoading: tasksLoading } = usePriorityStack(organizationId, !!organizationId);

  // Threads tab data (Spec 041). Keep polling across tabs so unread badges stay fresh.
  const {
    threads,
    unreadCount,
    isLoading: threadsLoading,
    error: threadsError,
    refresh: refreshThreads,
    createThread,
    archiveThread,
  } = useThreads(organizationId, !!organizationId);

  // Fleet tab data (Spec 041)
  const {
    fleet,
    isLoading: fleetLoading,
    error: fleetError,
    refresh: refreshFleet,
  } = useFleet(organizationId, !!organizationId && activeTab === 'fleet');

  useEffect(() => {
    setGatewayStatus(status);
  }, [status]);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // ─── Styles ───────────────────────────────────────────────────────────────

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#1F313A',
    borderLeft: '1px solid rgba(255,255,255,0.08)',
    overflow: 'hidden',
  };

  const panelHeaderStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    fontSize: '13px',
    fontWeight: 600,
    color: '#C8D6DF',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  };

  const tabBarStyle: React.CSSProperties = {
    display: 'flex',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    flexShrink: 0,
  };

  const tabStyle = (isActive: boolean): React.CSSProperties => ({
    flex: 1,
    padding: '7px 4px',
    fontSize: '11px',
    fontWeight: isActive ? 600 : 400,
    color: isActive ? '#4DC95A' : '#8899A6',
    background: 'none',
    border: 'none',
    borderBottom: isActive ? '2px solid #4DC95A' : '2px solid transparent',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'color 0.15s',
    letterSpacing: '0.3px',
  });

  const badgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: '#FF5656',
    color: '#fff',
    fontSize: '8px',
    fontWeight: 700,
    marginLeft: '4px',
    verticalAlign: 'middle',
  };

  const contentStyle: React.CSSProperties = {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  };

  // Status tab styles (carried over from original CommandCenterPanel)
  const sectionStyle: React.CSSProperties = { padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' };
  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '11px', fontWeight: 600, color: '#8899A6', textTransform: 'uppercase',
    letterSpacing: '0.5px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px',
  };
  const statusRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0',
    fontSize: '12px', color: '#C8D6DF',
  };
  const statusDotStyle = (connected: boolean): React.CSSProperties => ({
    width: '6px', height: '6px', borderRadius: '50%',
    backgroundColor: connected ? '#33AA40' : '#FF5656',
    boxShadow: connected ? '0 0 4px rgba(51,170,64,0.4)' : 'none',
  });
  const taskItemStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0',
    fontSize: '12px', color: '#C8D6DF',
  };
  const priorityBadgeStyle = (priority: string): React.CSSProperties => ({
    fontSize: '10px', fontWeight: 700, padding: '1px 5px', borderRadius: '3px',
    backgroundColor: `${PRIORITY_COLORS[priority] || '#8899A6'}20`,
    color: PRIORITY_COLORS[priority] || '#8899A6', textTransform: 'uppercase',
  });
  const statusBadgeStyle = (s: string): React.CSSProperties => ({
    fontSize: '10px', padding: '1px 5px', borderRadius: '3px',
    backgroundColor: `${STATUS_COLORS[s] || '#8899A6'}20`,
    color: STATUS_COLORS[s] || '#8899A6', marginLeft: 'auto', flexShrink: 0,
  });
  const sessionRowStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '4px 0', fontSize: '11px', color: '#8899A6',
  };
  const activeSessionStyle: React.CSSProperties = { ...sessionRowStyle, color: '#C8D6DF', fontWeight: 500 };
  const scrollAreaStyle: React.CSSProperties = { flex: 1, overflowY: 'auto' };
  const emptyStateStyle: React.CSSProperties = { padding: '20px 16px', textAlign: 'center', color: '#8899A6', fontSize: '12px' };
  const sessionKeyLabel = sessionKey === 'agent:main:main' ? 'Command Center (main)' : sessionKey;

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={containerStyle}>
      {/* Panel header */}
      <div style={panelHeaderStyle}>
        <ActivityIcon size={14} color="#4DC95A" />
        Command Center
      </div>

      {/* Tab bar */}
      <div style={tabBarStyle} role="tablist" aria-label="Command center sections">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'threads'}
          style={tabStyle(activeTab === 'threads')}
          onClick={() => setActiveTab('threads')}
        >
          Threads
          {unreadCount > 0 && activeTab !== 'threads' && (
            <span style={badgeStyle}>{unreadCount > 9 ? '9+' : unreadCount}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'fleet'}
          style={tabStyle(activeTab === 'fleet')}
          onClick={() => setActiveTab('fleet')}
        >
          Fleet
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'status'}
          style={tabStyle(activeTab === 'status')}
          onClick={() => setActiveTab('status')}
        >
          Status
        </button>
      </div>

      {/* Tab content */}
      <div style={contentStyle}>
        {activeTab === 'threads' && (
          <ThreadsTab
            threads={threads}
            isLoading={threadsLoading}
            error={threadsError}
            onRefresh={refreshThreads}
            onCreateThread={async (title) => { await createThread({ title }); }}
            onArchiveThread={archiveThread}
          />
        )}

        {activeTab === 'fleet' && (
          <FleetTab
            fleet={fleet}
            isLoading={fleetLoading}
            error={fleetError}
            onRefresh={refreshFleet}
          />
        )}

        {activeTab === 'status' && (
          <div style={scrollAreaStyle}>
            {/* Gateway Connection Status */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>
                <WifiIcon size={11} color="#8899A6" />
                Connection
              </div>
              <div style={statusRowStyle}>
                <span>Gateway</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={statusDotStyle(gatewayStatus.connected)} />
                  <span style={{ color: gatewayStatus.connected ? '#33AA40' : '#FF5656', fontSize: '11px' }}>
                    {gatewayStatus.connected ? 'Connected' : 'Disconnected'}
                  </span>
                </div>
              </div>
              <div style={statusRowStyle}>
                <span>Auth</span>
                <span style={{ color: gatewayStatus.authOk ? '#33AA40' : '#8899A6', fontSize: '11px' }}>
                  {gatewayStatus.authOk ? 'Verified' : 'Pending'}
                </span>
              </div>
              {gatewayStatus.gatewayVersion && (
                <div style={statusRowStyle}>
                  <span>Version</span>
                  <span style={{ fontSize: '11px' }}>{gatewayStatus.gatewayVersion}</span>
                </div>
              )}
              <div style={statusRowStyle}>
                <span>Session</span>
                <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>{sessionKeyLabel}</span>
              </div>
            </div>

            {/* Active Sessions */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>
                <ListIcon size={11} color="#8899A6" />
                Sessions
              </div>
              {sessions.length === 0 ? (
                <div style={emptyStateStyle}>
                  {sessionsLoading ? 'Loading...' : 'No active sessions'}
                </div>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.key}
                    style={session.key === sessionKey ? activeSessionStyle : sessionRowStyle}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: '10px' }}>
                      {session.key.length > 24 ? session.key.slice(0, 24) + '...' : session.key}
                    </span>
                    <span>{session.messageCount} msgs</span>
                  </div>
                ))
              )}
            </div>

            {/* Priority Stack */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>
                <AlertCircleIcon size={11} color="#8899A6" />
                Priority Stack
              </div>
              {tasks.length === 0 ? (
                <div style={emptyStateStyle}>
                  {tasksLoading ? 'Loading...' : 'Connect gateway to view tasks'}
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} style={taskItemStyle}>
                    <span style={priorityBadgeStyle(task.priority)}>{task.priority}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {task.title}
                    </span>
                    <span style={statusBadgeStyle(task.status)}>{task.status}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommandCenterPanel;
